'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Mail, Check, AlertCircle } from 'lucide-react';

interface NewsletterForm {
  email: string;
  name: string;
}

export default function NewsletterSignup() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const { register, handleSubmit, formState: { errors } } = useForm<NewsletterForm>();

  const onSubmit = async (data: NewsletterForm) => {
    setLoading(true);
    setApiError(null);
    try {
      const res = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: data.email, name: data.name }),
      });
      const json = await res.json();
      if (!res.ok) {
        setApiError(json.error || 'Subscription failed. Please try again.');
      } else {
        setSubmitted(true);
      }
    } catch {
      setApiError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-20 border-t" style={{ borderColor: 'var(--border)' }}>
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
        <div className="max-w-xl mx-auto text-center">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-5"
            style={{ backgroundColor: '#E05A4718', color: 'var(--primary)' }}
          >
            <Mail size={24} />
          </div>
          <h2 className="text-2xl font-bold mb-3" style={{ color: 'var(--foreground)' }}>
            Stay connected to the archive
          </h2>
          <p className="text-sm mb-8" style={{ color: 'var(--muted-foreground)' }}>
            Receive updates when new eras are covered, major contributions are published, and the community reaches
            new milestones.
          </p>

          {submitted ? (
            <div
              className="flex items-center justify-center gap-3 px-6 py-4 rounded-xl border"
              style={{ backgroundColor: '#2C5E3B18', borderColor: '#2C5E3B44', color: '#1E4028' }}
            >
              <Check size={18} />
              <span className="text-sm font-medium">
                You&apos;re on the list — we&apos;ll be in touch.
              </span>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                placeholder="Your name (optional)"
                {...register('name')}
                className="flex-1 px-4 py-2.5 rounded-lg border text-sm outline-none transition-colors duration-150 focus:border-primary"
                style={{
                  backgroundColor: 'var(--input)',
                  borderColor: 'var(--border)',
                  color: 'var(--foreground)',
                }}
              />
              <input
                type="email"
                placeholder="your@email.com"
                {...register('email', {
                  required: 'Email is required',
                  pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' },
                })}
                className="flex-1 px-4 py-2.5 rounded-lg border text-sm outline-none transition-colors duration-150 focus:border-primary"
                style={{
                  backgroundColor: 'var(--input)',
                  borderColor: errors.email ? 'var(--status-rejected)' : 'var(--border)',
                  color: 'var(--foreground)',
                }}
              />
              <button
                type="submit"
                disabled={loading}
                className="px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-150 active:scale-95 disabled:opacity-60 whitespace-nowrap"
                style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
              >
                {loading ? 'Subscribing…' : 'Subscribe'}
              </button>
            </form>
          )}
          {errors.email && (
            <p className="text-xs mt-2" style={{ color: 'var(--status-rejected)' }}>
              {errors.email.message}
            </p>
          )}
          {apiError && (
            <div className="flex items-center justify-center gap-2 mt-3 text-xs" style={{ color: '#E07070' }}>
              <AlertCircle size={13} />
              {apiError}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}